import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label:
      "En restaurantmeny bør være like enkel å oppdatere som daglig drift krever",
  },
  {
    id: "why",
    label: "Hvorfor er det viktig å endre restaurantmenyen online?",
  },
  {
    id: "what",
    label: "Hva bør en restaurant kunne endre online?",
  },
  {
    id: "workflow",
    label: "Hvordan fungerer det å endre en meny online i praksis?",
  },
  {
    id: "pdf",
    label: "Hvorfor er en PDF ofte upraktisk ved hyppige menyendringer?",
  },
  {
    id: "mistakes",
    label: "Hvilke feil skjer når restauranter oppdaterer menyer online?",
  },
  {
    id: "recommendation",
    label: "Hvordan ville jeg gjort dette som restauranteier?",
  },
  {
    id: "next-steps",
    label: "Hva bør du gjøre nå?",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place hjelper deg med å oppdatere menyen online",
  },
  {
    id: "faq",
    label: "FAQ om å endre restaurantmeny online",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>

      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function ChangeMenuOnlineNo() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Endre meny online: oppdater priser og retter selv | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/speisekarte-online-aendern",
          en: "/en/change-menu-online",
          no: "/no/endre-meny-online",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Endre menyen online: oppdater priser, retter og bilder selv
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                I en restaurant endrer noe seg hele tiden: en rett blir utsolgt,
                en pris må oppdateres, dagens tilbud legges til eller et bedre
                bilde er klart. Det er nettopp her en digital meny blir praktisk.
              </p>

              <p className="seoHero__text">
                I stedet for å eksportere en ny PDF, kontakte designer eller
                trykke menyen på nytt, kan restauranten oppdatere innholdet
                online. QR-koden forblir den samme, mens menyen bak den holdes
                oppdatert.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>
            En restaurantmeny bør være like enkel å oppdatere som daglig drift
            krever
          </h2>

          <p>
            Mange restauranter bruker fortsatt PDF-filer eller trykte menyer.
            Det kan fungere når lite endres. Men når priser, tilgjengelighet,
            dagens tilbud eller bilder endres regelmessig, blir en statisk meny
            raskt upraktisk.
          </p>

          <p>
            En digital meny er nyttig fordi restauranten kan endre innhold
            online uten å endre QR-koden. Det gjør hverdagsoppdateringer
            raskere, enklere og mindre avhengige av designere, trykkerier eller
            teknisk hjelp.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Treg arbeidsflyt</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Skriv ned endringen.</li>

                <li className="text__seo-li">
                  Spør designer eller medarbeider.
                </li>

                <li className="text__seo-li">Lag en ny PDF.</li>

                <li className="text__seo-li">Last opp filen igjen.</li>

                <li className="text__seo-li">Sjekk QR-kode-lenken.</li>

                <li className="text__seo-li">
                  Gjester kan fortsatt se gammel informasjon.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Bedre arbeidsflyt</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Åpne administrasjonsområdet.</li>

                <li className="text__seo-li">
                  Rediger rett, pris eller bilde.
                </li>

                <li className="text__seo-li">Lagre endringer.</li>

                <li className="text__seo-li">
                  Den digitale menyen oppdateres.
                </li>

                <li className="text__seo-li">QR-koden forblir den samme.</li>
              </ul>
            </div>
          </div>

          <h3>Test én liten oppdatering først</h3>

          <p>
            Legg inn noen ekte produkter, endre én pris og åpne menyen via
            QR-koden på en smarttelefon. Denne lille testen viser raskt om
            online menyredigering kan gjøre daglig arbeid enklere.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvorfor er det viktig å endre restaurantmenyen online?</h2>

            <p>
              En meny er ikke et statisk dokument. Den viser ikke bare hva
              restauranten tilbyr, men også om informasjonen er aktuell og
              pålitelig for gjestene.
            </p>

            <p>
              Hvis en gjest ser en rett online som ikke lenger er tilgjengelig,
              skaper det skuffelse. Hvis priser er utdaterte, kan det skape
              ubehagelige samtaler. Hvis dagens tilbud ikke vises, går muligheter
              tapt.
            </p>

            <h3>Online oppdateringer støtter daglig restaurantdrift</h3>

            <p>
              Restaurantteknologi er mest nyttig når den løser praktiske
              problemer: raskere oppdateringer, færre utdaterte menyer,
              tydeligere informasjon til gjester og mindre avhengighet av
              trykt materiale.
            </p>

            <p>
              Når gjester søker digitalt, skanner QR-koder og åpner menyer på
              telefonen, bør menyinformasjonen også være enkel å holde oppdatert
              online.
            </p>
          </div>
        </section>

        <section id="what" className="acquaintance">
          <h2>Hva bør en restaurant kunne endre online?</h2>

          <p>
            En god digital meny bør ikke bare vise det som ble lagt inn én gang.
            Den bør la eieren eller teamet administrere det viktigste innholdet
            direkte.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Endre priser.</li>
            <li className="text__seo-li">Legge til retter.</li>
            <li className="text__seo-li">Skjule produkter.</li>
            <li className="text__seo-li">Publisere dagens tilbud.</li>
            <li className="text__seo-li">Bytte bilder.</li>
            <li className="text__seo-li">Forbedre beskrivelser.</li>
            <li className="text__seo-li">Sortere kategorier.</li>
            <li className="text__seo-li">Justere tilgjengelighet.</li>
          </ul>

          <p>
            Muligheten til å skjule produkter midlertidig er spesielt viktig.
            Hvis en rett er utsolgt, bør den ikke fortsatt vises som om den var
            tilgjengelig.
          </p>

          <VisualIdea>
            Et administrasjonspanel med tre enkle handlinger: endre pris,
            skjule rett, bytte bilde. Ved siden av vises en smarttelefon med den
            oppdaterte menyen.
          </VisualIdea>
        </section>

        <section id="workflow" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvordan fungerer det å endre en meny online i praksis?</h2>

            <p>
              Arbeidsflyten bør være enkel nok til at ekstern hjelp ikke trengs
              for hver lille endring. En restauranteier, leder eller ansvarlig
              medarbeider bør kunne håndtere typiske oppdateringer.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                <strong>Logg inn:</strong> Eieren åpner administrasjonsområdet.
              </li>

              <li className="text__seo-li">
                <strong>Velg produkt:</strong> Velg en rett, drikke eller dagens
                tilbud.
              </li>

              <li className="text__seo-li">
                <strong>Rediger innhold:</strong> Juster pris, beskrivelse,
                bilde eller synlighet.
              </li>

              <li className="text__seo-li">
                <strong>Lagre:</strong> Den offentlige menyen oppdateres.
              </li>

              <li className="text__seo-li">
                <strong>Sjekk QR-koden:</strong> Den eksisterende QR-koden åpner
                fortsatt den oppdaterte menysiden.
              </li>
            </ol>

            <p>
              Hovedpoenget: QR-koden trenger ikke å trykkes på nytt så lenge
              menylenken forblir stabil.
            </p>
          </div>
        </section>

        <section id="pdf" className="acquaintance">
          <h2>Hvorfor er en PDF ofte upraktisk ved hyppige menyendringer?</h2>

          <p>
            En PDF virker enkel i starten. Du tar den eksisterende menyen,
            eksporterer den og kobler den til en QR-kode. Ved sjeldne endringer
            kan det være nok.
          </p>

          <p>
            Det blir vanskelig når menyen endres regelmessig. Hver oppdatering
            krever redigering, eksport, opplasting og ny testing.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>PDF-meny</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Ofte vanskelig å lese på små skjermer.
                </li>

                <li className="text__seo-li">
                  Endringer krever flere steg.
                </li>

                <li className="text__seo-li">
                  Gamle versjoner kan fortsatt være i omløp.
                </li>

                <li className="text__seo-li">
                  Ikke ideell for dagens tilbud.
                </li>

                <li className="text__seo-li">
                  Ingen fleksibel produktadministrasjon.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Online-administrert meny</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Strukturert for smarttelefon.</li>

                <li className="text__seo-li">
                  Priser kan redigeres direkte.
                </li>

                <li className="text__seo-li">
                  Utsolgte produkter kan skjules.
                </li>

                <li className="text__seo-li">
                  Bilder og kategorier er enklere å administrere.
                </li>

                <li className="text__seo-li">QR-koden kan forbli den samme.</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvilke feil skjer når restauranter oppdaterer menyer online?</h2>

            <p>
              Online oppdateringer hjelper bare når de er organisert tydelig.
              Ellers kan selv en digital meny bli forvirrende.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Vanlige feil</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Priser oppdateres ikke online og offline samtidig.
                  </li>

                  <li className="text__seo-li">
                    Utsolgte retter forblir synlige.
                  </li>

                  <li className="text__seo-li">
                    Flere PDF-versjoner sirkulerer.
                  </li>

                  <li className="text__seo-li">
                    Bilder lastes opp uten konsekvent stil.
                  </li>

                  <li className="text__seo-li">
                    Kategorier blir for lange eller rotete.
                  </li>

                  <li className="text__seo-li">
                    QR-koden peker til en gammel fil.
                  </li>

                  <li className="text__seo-li">
                    Ingen har ansvar for menyvedlikehold.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Bedre</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Bruk én sentral digital meny.
                  </li>

                  <li className="text__seo-li">Lagre endringer med en gang.</li>

                  <li className="text__seo-li">
                    Skjul utilgjengelige produkter midlertidig.
                  </li>

                  <li className="text__seo-li">
                    Velg bilder med tydelig hensikt.
                  </li>

                  <li className="text__seo-li">
                    Rydd opp i kategorier regelmessig.
                  </li>

                  <li className="text__seo-li">Bruk en stabil menylenke.</li>

                  <li className="text__seo-li">
                    Gi én person ansvar for menyen.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>Hvordan ville jeg gjort dette som restauranteier?</h2>

          <p>
            Jeg ville ikke prøvd å digitalisere hele menyen perfekt med en gang.
            Jeg ville startet med de delene som endres oftest.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Start med dagens tilbud:</strong> Nytten blir synlig
              raskere enn med en meny som sjelden endres.
            </li>

            <li className="text__seo-li">
              <strong>Test prisoppdateringer:</strong> Endre én pris og sjekk om
              oppdateringen vises med en gang.
            </li>

            <li className="text__seo-li">
              <strong>Skjul én rett:</strong> Simuler at et produkt er utsolgt.
            </li>

            <li className="text__seo-li">
              <strong>Bytt ett bilde:</strong> Sjekk om bilder forbedrer
              presentasjonen.
            </li>

            <li className="text__seo-li">
              <strong>Hold QR-koden stabil:</strong> Bruk samme QR-kode mens
              innholdet endres.
            </li>

            <li className="text__seo-li">
              <strong>Involver teamet:</strong> Minst én person bør vite hvordan
              oppdateringer gjøres.
            </li>
          </ol>

          <p>
            Dette gjør den digitale menyen nyttig i ekte arbeid, ikke bare som
            en pen online presentasjon.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hva bør du gjøre nå?</h2>

            <p>
              Hvis du vil endre restaurantmenyen online, start med en liten
              praktisk test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Opprett en digital meny.</li>

              <li className="text__seo-li">Legg til 10 til 20 ekte produkter.</li>

              <li className="text__seo-li">Endre én pris.</li>

              <li className="text__seo-li">
                Skjul ett produkt som en test.
              </li>

              <li className="text__seo-li">
                Åpne menyen via QR-koden.
              </li>

              <li className="text__seo-li">
                Sjekk visningen på en smarttelefon.
              </li>

              <li className="text__seo-li">
                Bestem deretter om du vil legge til flere kategorier.
              </li>
            </ul>

            <VisualIdea>
              Et enkelt administrasjonspanel der restauranteieren endrer én
              pris, skjuler ett utsolgt produkt og umiddelbart sjekker den
              oppdaterte menyen på en smarttelefon.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="no" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ om å endre restaurantmeny online</h2>

          <div className="seoFaq">
            <details>
              <summary>Kan jeg endre menypriser selv online?</summary>

              <p>
                Ja, hvis menyen er bygget som en digital meny med et
                administrasjonsområde. Da kan priser redigeres og lagres direkte.
              </p>
            </details>

            <details>
              <summary>
                Må jeg trykke QR-koden på nytt etter hver endring?
              </summary>

              <p>
                Nei, ikke hvis QR-koden peker til en stabil menylenke.
                Menyinnholdet endres, men QR-koden forblir den samme.
              </p>
            </details>

            <details>
              <summary>Er en PDF-meny nok?</summary>

              <p>
                Ved sjeldne endringer kan en PDF være nok. Hvis priser, dagens
                tilbud eller tilgjengelighet endres oftere, er en
                online-administrert meny mer praktisk.
              </p>
            </details>

            <details>
              <summary>Kan jeg skjule utsolgte retter?</summary>

              <p>
                Ja. Det er en av de mest nyttige fordelene med en digital meny.
                Et produkt kan skjules midlertidig og vises igjen senere.
              </p>
            </details>

            <details>
              <summary>Hvem bør vedlikeholde menyen?</summary>

              <p>
                Ideelt sett en person som kjenner den daglige restaurantdriften:
                eieren, lederen eller en ansvarlig medarbeider.
              </p>
            </details>

            <details>
              <summary>Kan jeg starte med bare en del av menyen?</summary>

              <p>
                Ja. Én kategori, noen få ekte produkter og en rask QR-kode-test
                er nok for et nyttig første steg.
              </p>
            </details>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Flere temaer om digitale menyer</h2>

          <div className="seoCompareSmall">
            <p>
              <Link to="/no/digital-meny-for-restauranter">
                Digital meny for restauranter
              </Link>
            </p>

            <p>
              <Link to="/no/qr-kode-meny-for-restauranter">
                Lag en QR-kode-meny
              </Link>
            </p>

            <p>
              <Link to="/no/gratis-digital-meny-for-restauranter">
                Test en digital meny gratis
              </Link>
            </p>

            <p>
              <Link to="/no/flerspraklig-meny-for-restauranter">
                Flerspråklig meny for restauranter
              </Link>
            </p>

            <p>
              <Link to="/no/qr-kode-meny-utskrift">
                Utskrift av QR-koder for restaurantmenyer
              </Link>
            </p>

            <p>
              <Link to="/no/opprett-digital-dagsmeny">
                Daglig oppdatering av spesialtilbud
              </Link>
            </p>
          </div>
        </section>
      </article>
    </main>
  );
}